import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { accountingApi } from '../../../services/accountingApi';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from '../exportUtils';

const typeBadge = (type) => {
  if (!type) return 'acc-badge-gray';
  const t = type.toLowerCase();
  if (t.includes('asset')) return 'acc-badge-blue';
  if (t.includes('liabil')) return 'acc-badge-red';
  if (t.includes('equity')) return 'acc-badge-green';
  if (t.includes('revenue') || t.includes('income')) return 'acc-badge-blue';
  if (t.includes('expense')) return 'acc-badge-yellow';
  return 'acc-badge-gray';
};

const fmt = (n) => {
  const num = parseFloat(n) || 0;
  return '$ ' + num.toLocaleString('en-US', { minimumFractionDigits: 2 });
};

const ChartOfAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [ledger, setLedger] = useState(null);
  const [ledgerAccount, setLedgerAccount] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  // Ledger Column Visibility & Export
  const [visibleColumns, setVisibleColumns] = useState({
    date: true,
    description: true,
    note: true,
    added_by: true,
    debit: true,
    credit: true,
    action: true
  });
  const [showColumnVisMenu, setShowColumnVisMenu] = useState(false);

  // View Mode: 'tree' or 'tabular'
  const [viewMode, setViewMode] = useState('tree');
  const [expandedNodes, setExpandedNodes] = useState({
    Asset: true,
    Liability: true,
    Equity: true,
    Income: true,
    Expenses: true,
  });

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editAccount, setEditAccount] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', gl_code: '', account_type_id: '', opening_balance: '0' });

  const [treeList, setTreeList] = useState([]);

  const loadData = () => {
    setLoading(true);
    Promise.all([accountingApi.getAccounts(), accountingApi.getAccountTypes(), accountingApi.getTreeData()])
      .then(([accsRes, typesRes, treeRes]) => {
        const toArr = r => Array.isArray(r) ? r : Array.isArray(r.data) ? r.data : Array.isArray(r.data?.data) ? r.data.data : [];
        setAccounts(toArr(accsRes));
        setAccountTypes(toArr(typesRes));

        const treeArr = toArr(treeRes);
        setTreeList(treeArr);

        // Expand root primary types and sub-types by default
        const initExpand = {};
        treeArr.forEach(group => {
          const pKey = group.label || group.name;
          initExpand[pKey] = true;
          (group.sub_types || []).forEach(sub => {
            initExpand[`${pKey}->${sub.name}`] = true;
          });
        });
        setExpandedNodes(initExpand);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  const typeNames = ['All', ...accountTypes.map(t => t.label || t.name || t.account_type || t.type || t.key || String(t.id || '')).filter(Boolean)];

  const filtered = accounts.filter(a => {
    const typeName = a.account_primary_type || a.account_sub_type || a.account_type?.name || a.type || '';
    const matchType = filterType === 'All' || typeName.toLowerCase() === filterType.toLowerCase();
    const matchSearch = (a.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (a.gl_code || a.code || '').toString().includes(search);
    return matchType && matchSearch;
  });

  const toggleNode = (key) => {
    setExpandedNodes(prev => ({ ...prev, [key]: !(prev[key] ?? true) }));
  };

  const expandAll = () => {
    const allKeys = {};
    treeList.forEach(group => {
      const pKey = group.label || group.name;
      allKeys[pKey] = true;
      (group.sub_types || []).forEach(sub => {
        allKeys[`${pKey}->${sub.name}`] = true;
      });
    });
    setExpandedNodes(allKeys);
  };

  const collapseAll = () => {
    const allKeys = {};
    treeList.forEach(group => {
      const pKey = group.label || group.name;
      allKeys[pKey] = false;
      (group.sub_types || []).forEach(sub => {
        allKeys[`${pKey}->${sub.name}`] = false;
      });
    });
    setExpandedNodes(allKeys);
  };

  const [ledgerAccountInfo, setLedgerAccountInfo] = useState(null);
  const [ledgerTotals, setLedgerTotals] = useState({ debit: 0, credit: 0 });
  const [ledgerSearch, setLedgerSearch] = useState('');
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerPerPage, setLedgerPerPage] = useState(25);

  const openLedger = async (account) => {
    setLedgerAccount(account);
    setLedgerLoading(true);
    setLedger(null);
    setLedgerPage(1);
    setLedgerSearch('');
    try {
      const res = await accountingApi.getAccountLedger(account.id);
      setLedgerAccountInfo(res.account || null);
      setLedgerTotals({ debit: res.total_debit || 0, credit: res.total_credit || 0 });
      setLedger(res.data || []);
    } catch (e) {
      setLedger([]);
    } finally {
      setLedgerLoading(false);
    }
  };

  const openAddModal = () => {
    setEditAccount(null);
    setForm({ name: '', gl_code: '', account_type_id: accountTypes[0]?.id || '', opening_balance: '0' });
    setShowModal(true);
  };

  const openEditModal = (account) => {
    setEditAccount(account);
    setForm({
      name: account.name || '',
      gl_code: account.gl_code || account.code || '',
      account_type_id: account.account_type_id || account.account_type?.id || '',
      opening_balance: account.opening_balance || account.current_balance || '0'
    });
    setShowModal(true);
  };

  const handleSaveAccount = async () => {
    if (!form.name) {
      toast.warning('Account Name is required');
      return;
    }
    setSaving(true);
    try {
      if (editAccount) {
        await accountingApi.updateAccount(editAccount.id, form);
        toast.success('Account updated successfully!');
      } else {
        await accountingApi.createAccount(form);
        toast.success('Account created successfully!');
      }
      setShowModal(false);
      loadData();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm('Are you sure you want to delete this account?')) return;
    try {
      await accountingApi.deleteAccount(id);
      toast.success('Account deleted successfully!');
      loadData();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    }
  };

  // Group accounts for Tree View
  const groupTree = () => {
    const categories = {
      Asset: {},
      Liability: {},
      Equity: {},
      Income: {},
      Expenses: {},
    };

    filtered.forEach(a => {
      let rawType = (a.account_primary_type || 'asset').toLowerCase();
      let p = 'Asset';
      if (rawType.includes('asset')) p = 'Asset';
      else if (rawType.includes('liabil')) p = 'Liability';
      else if (rawType.includes('equity')) p = 'Equity';
      else if (rawType.includes('income') || rawType.includes('revenue')) p = 'Income';
      else if (rawType.includes('expense')) p = 'Expenses';

      if (!categories[p]) categories[p] = {};

      let sub = a.account_sub_type || 'General';
      if (!categories[p][sub]) categories[p][sub] = [];
      categories[p][sub].push(a);
    });

    return categories;
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading accounts...</div>;
  if (error) return <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>Error: {error}</div>;

  const treeData = groupTree();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

      {/* Header & View Mode Switcher */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.4rem', fontWeight: 800, color: '#1e293b' }}>Chart of accounts</h2>
          <div style={{ display: 'flex', gap: '0.25rem', background: '#e2e8f0', padding: '0.25rem', borderRadius: '8px', width: 'fit-content' }}>
            <button
              onClick={() => setViewMode('tree')}
              style={{
                padding: '0.4rem 0.9rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                background: viewMode === 'tree' ? '#0ea5e9' : 'transparent',
                color: viewMode === 'tree' ? '#fff' : '#475569',
                boxShadow: viewMode === 'tree' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                display: 'flex', alignItems: 'center', gap: '0.35rem'
              }}
            >
              📊 Tree view
            </button>
            <button
              onClick={() => setViewMode('tabular')}
              style={{
                padding: '0.4rem 0.9rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                background: viewMode === 'tabular' ? '#0ea5e9' : 'transparent',
                color: viewMode === 'tabular' ? '#fff' : '#475569',
                boxShadow: viewMode === 'tabular' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                display: 'flex', alignItems: 'center', gap: '0.35rem'
              }}
            >
              📋 Tabular view
            </button>
          </div>
        </div>

        <button
          className="acc-btn-primary"
          onClick={openAddModal}
          style={{ background: '#4f46e5', color: '#fff', borderRadius: '20px', padding: '0.55rem 1.4rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.4rem', boxShadow: '0 4px 6px -1px rgba(79,70,229,0.3)' }}
        >
          <span style={{ fontSize: '1.1rem' }}>+</span> Add
        </button>
      </div>

      {ledger !== null ? (
        /* ── 100% WEB ALIGNED LEDGER VIEW ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button
              onClick={() => { setLedger(null); setLedgerAccount(null); }}
              className="acc-btn-secondary"
              style={{ padding: '0.45rem 1rem', fontSize: '0.85rem', fontWeight: 600 }}
            >
              ← Back
            </button>
            <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 800, color: '#1e293b' }}>
              Ledger - {ledgerAccountInfo?.name || ledgerAccount?.name}
            </h2>
          </div>

          {/* Top Cards Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.25rem' }}>
            {/* Left Card: Account Info */}
            <div style={{ background: '#fff', borderRadius: '12px', padding: '1.25rem', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
              <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.5rem 0', fontWeight: 700, color: '#475569', width: '40%' }}>Name:</td>
                    <td style={{ padding: '0.5rem 0', fontWeight: 600, color: '#0f172a' }}>{ledgerAccountInfo?.name || ledgerAccount?.name}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.5rem 0', fontWeight: 700, color: '#475569' }}>Account Type:</td>
                    <td style={{ padding: '0.5rem 0', fontWeight: 500, color: '#334155' }}>{ledgerAccountInfo?.account_primary_type || '—'}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.5rem 0', fontWeight: 700, color: '#475569' }}>Account Sub Type:</td>
                    <td style={{ padding: '0.5rem 0', fontWeight: 500, color: '#334155' }}>{ledgerAccountInfo?.account_sub_type || '—'}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.5rem 0', fontWeight: 700, color: '#475569' }}>Detail Type:</td>
                    <td style={{ padding: '0.5rem 0', fontWeight: 500, color: '#334155' }}>{ledgerAccountInfo?.detail_type || '—'}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.5rem 0', fontWeight: 700, color: '#475569' }}>Balance:</td>
                    <td style={{ padding: '0.5rem 0', fontWeight: 800, color: '#0f172a' }}>{fmt(ledgerAccountInfo?.balance ?? ledgerAccount?.current_balance ?? 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Right Card: Filters */}
            <div style={{ background: '#fff', borderRadius: '12px', padding: '1.25rem', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '1rem', fontWeight: 800, color: '#1e293b', fontSize: '0.95rem' }}>
                <span>🎯</span> Filters:
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#475569', marginBottom: '0.35rem' }}>Date Range:</label>
                  <input
                    type="text"
                    readOnly
                    value="08/07/2026 - 08/13/2026"
                    style={{ width: '100%', padding: '0.45rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#f8fafc', fontSize: '0.85rem', color: '#334155' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#475569', marginBottom: '0.35rem' }}>Account:</label>
                  <select
                    value={ledgerAccount?.id || ''}
                    onChange={(e) => {
                      const selected = accounts.find(a => String(a.id) === String(e.target.value));
                      if (selected) openLedger(selected);
                    }}
                    style={{ width: '100%', padding: '0.45rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', color: '#1e293b', background: '#fff' }}
                  >
                    {accounts.map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Ledger Transactions Table Card */}
          {(() => {
            const filteredLedger = (ledger || []).filter(row => {
              if (!ledgerSearch) return true;
              const s = ledgerSearch.toLowerCase();
              return (
                (row.date || '').toLowerCase().includes(s) ||
                (row.description || '').toLowerCase().includes(s) ||
                (row.note || '').toLowerCase().includes(s) ||
                (row.added_by || '').toLowerCase().includes(s)
              );
            });

            const totalLedgerItems = filteredLedger.length;
            const totalPages = Math.ceil(totalLedgerItems / ledgerPerPage) || 1;
            const startIndex = (ledgerPage - 1) * ledgerPerPage;
            const paginatedLedger = filteredLedger.slice(startIndex, startIndex + ledgerPerPage);
            const pageTotalDebit = paginatedLedger.reduce((sum, row) => sum + (parseFloat(row.debit) || 0), 0);
            const pageTotalCredit = paginatedLedger.reduce((sum, row) => sum + (parseFloat(row.credit) || 0), 0);

            const handleLedgerExport = (type) => {
              const dataToExport = filteredLedger || [];
              if (dataToExport.length === 0) {
                toast.warning('No ledger entries available to export');
                return;
              }

              const headers = [];
              if (visibleColumns.date) headers.push('Date');
              if (visibleColumns.description) headers.push('Description');
              if (visibleColumns.note) headers.push('Note');
              if (visibleColumns.added_by) headers.push('Added By');
              if (visibleColumns.debit) headers.push('Debit');
              if (visibleColumns.credit) headers.push('Credit');

              const rows = dataToExport.map(row => {
                const r = [];
                if (visibleColumns.date) r.push(row.date || '—');
                if (visibleColumns.description) r.push(row.description || '—');
                if (visibleColumns.note) r.push(row.note || '—');
                if (visibleColumns.added_by) r.push(row.added_by || '—');
                if (visibleColumns.debit) r.push(row.debit ? parseFloat(row.debit).toFixed(2) : '');
                if (visibleColumns.credit) r.push(row.credit ? parseFloat(row.credit).toFixed(2) : '');
                return r;
              });

              const accountName = ledgerAccount?.name || 'Account';
              const filename = `ledger_${accountName.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;

              if (type === 'Export CSV') exportToCSV(headers, rows, filename);
              if (type === 'Export Excel') exportToExcel(headers, rows, filename);
              if (type === 'Export PDF') exportToPDF(`Ledger — ${accountName}`, headers, rows, filename);
              if (type === 'Print') printPage();
            };

            const visibleTextCols = ['date', 'description', 'note', 'added_by'].filter(k => visibleColumns[k]).length;
            const totalVisibleCols = Object.keys(visibleColumns).filter(k => visibleColumns[k]).length;

            return (
              <div style={{ background: '#fff', borderRadius: '12px', padding: '1.25rem', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                {/* Table Action Bar */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: '#475569' }}>
                    <span>Show</span>
                    <select
                      value={ledgerPerPage}
                      onChange={e => { setLedgerPerPage(Number(e.target.value)); setLedgerPage(1); }}
                      style={{ padding: '0.2rem 0.4rem', borderRadius: '4px', border: '1px solid #cbd5e1', outline: 'none', cursor: 'pointer' }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span>entries</span>
                  </div>

                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', position: 'relative' }}>
                    {['Export CSV', 'Export Excel', 'Print', 'Export PDF'].map(btn => (
                      <button
                        key={btn}
                        onClick={() => handleLedgerExport(btn)}
                        style={{ padding: '0.3rem 0.65rem', borderRadius: '15px', border: '1px solid #cbd5e1', background: '#fff', fontSize: '0.75rem', fontWeight: 600, color: '#334155', cursor: 'pointer', transition: 'all 0.15s' }}
                        onMouseEnter={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.color = '#2563eb'; }}
                        onMouseLeave={e => { e.target.style.borderColor = '#cbd5e1'; e.target.style.color = '#334155'; }}
                      >
                        {btn}
                      </button>
                    ))}

                    <div style={{ position: 'relative' }}>
                      <button
                        onClick={() => setShowColumnVisMenu(!showColumnVisMenu)}
                        style={{ padding: '0.3rem 0.65rem', borderRadius: '15px', border: showColumnVisMenu ? '1px solid #3b82f6' : '1px solid #cbd5e1', background: showColumnVisMenu ? '#eff6ff' : '#fff', fontSize: '0.75rem', fontWeight: 600, color: showColumnVisMenu ? '#2563eb' : '#334155', cursor: 'pointer' }}
                      >
                        Column visibility ▾
                      </button>
                      {showColumnVisMenu && (
                        <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '6px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', zIndex: 100, padding: '0.5rem', minWidth: '160px' }}>
                          <div style={{ fontSize: '0.72rem', fontWeight: 700, marginBottom: '0.4rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '0 0.2rem' }}>Toggle Columns</div>
                          {[
                            { key: 'date', label: 'Date' },
                            { key: 'description', label: 'Description' },
                            { key: 'note', label: 'Note' },
                            { key: 'added_by', label: 'Added By' },
                            { key: 'debit', label: 'Debit' },
                            { key: 'credit', label: 'Credit' },
                            { key: 'action', label: 'Action' },
                          ].map(col => (
                            <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', padding: '0.3rem 0.4rem', cursor: 'pointer', borderRadius: '4px', background: visibleColumns[col.key] ? '#f8fafc' : 'transparent' }}>
                              <input
                                type="checkbox"
                                checked={visibleColumns[col.key]}
                                onChange={() => setVisibleColumns(prev => ({ ...prev, [col.key]: !prev[col.key] }))}
                              />
                              {col.label}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '0.3rem 0.6rem', background: '#fff' }}>
                    <input
                      placeholder="Search..."
                      value={ledgerSearch}
                      onChange={e => { setLedgerSearch(e.target.value); setLedgerPage(1); }}
                      style={{ border: 'none', outline: 'none', fontSize: '0.85rem', width: '140px' }}
                    />
                  </div>
                </div>

                {/* Table */}
                {ledgerLoading ? (
                  <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading ledger entries...</div>
                ) : (
                  <>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="coa-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                            {visibleColumns.date && <th style={{ textAlign: 'left', padding: '0.65rem' }}>Date</th>}
                            {visibleColumns.description && <th style={{ textAlign: 'left', padding: '0.65rem' }}>Description</th>}
                            {visibleColumns.note && <th style={{ textAlign: 'left', padding: '0.65rem' }}>Note</th>}
                            {visibleColumns.added_by && <th style={{ textAlign: 'left', padding: '0.65rem' }}>Added By</th>}
                            {visibleColumns.debit && <th style={{ textAlign: 'right', padding: '0.65rem' }}>Debit</th>}
                            {visibleColumns.credit && <th style={{ textAlign: 'right', padding: '0.65rem' }}>Credit</th>}
                            {visibleColumns.action && <th style={{ textAlign: 'center', padding: '0.65rem' }}>Action</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedLedger.length === 0 && (
                            <tr>
                              <td colSpan={totalVisibleCols || 7} style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                                No ledger entries found
                              </td>
                            </tr>
                          )}
                          {paginatedLedger.map((row, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              {visibleColumns.date && <td style={{ padding: '0.65rem', whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{row.date || '—'}</td>}
                              {visibleColumns.description && <td style={{ padding: '0.65rem', fontSize: '0.85rem', whiteSpace: 'pre-line' }}>{row.description || '—'}</td>}
                              {visibleColumns.note && <td style={{ padding: '0.65rem', fontSize: '0.85rem' }}>{row.note || '—'}</td>}
                              {visibleColumns.added_by && <td style={{ padding: '0.65rem', fontSize: '0.85rem', fontWeight: 500 }}>{row.added_by || '—'}</td>}
                              {visibleColumns.debit && (
                                <td style={{ padding: '0.65rem', textAlign: 'right', fontWeight: 600, color: row.debit ? '#0f172a' : '#94a3b8' }}>
                                  {row.debit ? fmt(row.debit) : ''}
                                </td>
                              )}
                              {visibleColumns.credit && (
                                <td style={{ padding: '0.65rem', textAlign: 'right', fontWeight: 600, color: row.credit ? '#0f172a' : '#94a3b8' }}>
                                  {row.credit ? fmt(row.credit) : ''}
                                </td>
                              )}
                              {visibleColumns.action && <td style={{ padding: '0.65rem', textAlign: 'center' }}>—</td>}
                            </tr>
                          ))}
                        </tbody>
                        {paginatedLedger.length > 0 && (
                          <tfoot>
                            <tr style={{ background: '#e2e8f0', fontWeight: 700 }}>
                              {visibleTextCols > 0 && (
                                <td colSpan={visibleTextCols} style={{ textAlign: 'center', padding: '0.75rem' }}>Total:</td>
                              )}
                              {visibleColumns.debit && <td style={{ textAlign: 'right', padding: '0.75rem' }}>{fmt(pageTotalDebit)}</td>}
                              {visibleColumns.credit && <td style={{ textAlign: 'right', padding: '0.75rem' }}>{fmt(pageTotalCredit)}</td>}
                              {visibleColumns.action && <td></td>}
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>

                    {/* DataTables Pagination Controls */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem', flexWrap: 'wrap', gap: '1rem', fontSize: '0.85rem', color: '#475569' }}>
                      <div>
                        Showing {totalLedgerItems === 0 ? 0 : startIndex + 1} to {Math.min(startIndex + ledgerPerPage, totalLedgerItems)} of {totalLedgerItems} entries
                      </div>

                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button
                          disabled={ledgerPage <= 1}
                          onClick={() => setLedgerPage(p => Math.max(1, p - 1))}
                          style={{
                            padding: '0.35rem 0.75rem', borderRadius: '4px', border: '1px solid #cbd5e1',
                            background: ledgerPage <= 1 ? '#f1f5f9' : '#fff',
                            color: ledgerPage <= 1 ? '#94a3b8' : '#1e293b',
                            cursor: ledgerPage <= 1 ? 'not-allowed' : 'pointer', fontWeight: 600
                          }}
                        >
                          Previous
                        </button>
                        {(() => {
                          const getPaginationRange = (current, total) => {
                            if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
                            const pages = [1];
                            if (current > 3) pages.push('...');
                            const start = Math.max(2, current - 1);
                            const end = Math.min(total - 1, current + 1);
                            for (let i = start; i <= end; i++) pages.push(i);
                            if (current < total - 2) pages.push('...');
                            pages.push(total);
                            return pages;
                          };

                          return getPaginationRange(ledgerPage, totalPages).map((p, idx) => (
                            p === '...' ? (
                              <span key={`ellipsis-${idx}`} style={{ padding: '0.35rem 0.5rem', color: '#94a3b8' }}>...</span>
                            ) : (
                              <button
                                key={p}
                                onClick={() => setLedgerPage(p)}
                                style={{
                                  padding: '0.35rem 0.75rem', borderRadius: '4px', border: '1px solid #cbd5e1',
                                  background: ledgerPage === p ? '#2563eb' : '#fff',
                                  color: ledgerPage === p ? '#fff' : '#1e293b',
                                  fontWeight: ledgerPage === p ? 700 : 500,
                                  cursor: 'pointer'
                                }}
                              >
                                {p}
                              </button>
                            )
                          ));
                        })()}
                        <button
                          disabled={ledgerPage >= totalPages}
                          onClick={() => setLedgerPage(p => Math.min(totalPages, p + 1))}
                          style={{
                            padding: '0.35rem 0.75rem', borderRadius: '4px', border: '1px solid #cbd5e1',
                            background: ledgerPage >= totalPages ? '#f1f5f9' : '#fff',
                            color: ledgerPage >= totalPages ? '#94a3b8' : '#1e293b',
                            cursor: ledgerPage >= totalPages ? 'not-allowed' : 'pointer', fontWeight: 600
                          }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })()}
        </div>
      ) : viewMode === 'tree' ? (
        /* ── TREE VIEW ── */
        <div className="table-content-card" style={{ padding: '1.5rem', background: '#fff', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
          {/* Controls Bar */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '0.35rem 0.6rem', width: '100%', maxWidth: '360px', background: '#fff' }}>
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ border: 'none', outline: 'none', width: '100%', fontSize: '0.9rem' }}
              />
              <span style={{ color: '#94a3b8' }}>🔍</span>
            </div>
            <button
              onClick={expandAll}
              style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.45rem 1rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
            >
              Expand all
            </button>
            <button
              onClick={collapseAll}
              style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.45rem 1rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
            >
              Collapse all
            </button>
          </div>

          {/* Tree Component Hierarchy */}
          <div style={{ fontFamily: 'inherit', fontSize: '0.9rem', color: '#334155' }}>
            {treeList.map(group => {
              const primaryLabel = group.label || group.name;
              const isPrimaryExpanded = expandedNodes[primaryLabel] ?? true;

              return (
                <div key={primaryLabel} style={{ marginBottom: '1rem' }}>
                  {/* Primary Node (e.g. Asset) */}
                  <div
                    onClick={() => toggleNode(primaryLabel)}
                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 700, color: '#0f172a', padding: '0.25rem 0' }}
                  >
                    <span style={{ color: '#64748b' }}>{isPrimaryExpanded ? '📂' : '📁'}</span>
                    <span>{primaryLabel}</span>
                  </div>

                  {/* Subtypes under Primary Node */}
                  {isPrimaryExpanded && (
                    <div style={{ paddingLeft: '1.5rem', borderLeft: '1px dashed #cbd5e1', marginLeft: '0.4rem', marginTop: '0.25rem' }}>
                      {(group.sub_types || []).map(sub => {
                        const subKey = `${primaryLabel}->${sub.name}`;
                        const isSubExpanded = expandedNodes[subKey] ?? true;

                        const accList = (sub.accounts || []).filter(a => {
                          if (!search) return true;
                          const s = search.toLowerCase();
                          return (a.name || '').toLowerCase().includes(s) || (a.gl_code || '').toString().includes(s);
                        });

                        if (search && accList.length === 0) return null;

                        return (
                          <div key={sub.id} style={{ marginBottom: '0.75rem' }}>
                            <div
                              onClick={() => toggleNode(subKey)}
                              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, color: '#334155', padding: '0.2rem 0' }}
                            >
                              <span style={{ color: '#64748b' }}>{isSubExpanded ? '📂' : '📁'}</span>
                              <span>{sub.name}</span>
                            </div>

                            {/* Accounts under Subtype */}
                            {isSubExpanded && (
                              <div style={{ paddingLeft: '1.5rem', borderLeft: '1px dashed #cbd5e1', marginLeft: '0.4rem', marginTop: '0.25rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                {accList.map(acc => (
                                  <div key={acc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.15rem 0' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                      <span style={{ fontSize: '0.65rem', color: '#64748b' }}>🔘</span>
                                      <span style={{ fontWeight: 500, color: '#1e293b' }}>
                                        {acc.name} {acc.gl_code ? `(${acc.gl_code})` : ''} - <span style={{ fontWeight: 600, color: '#475569' }}>{fmt(acc.balance)}</span>
                                      </span>
                                      {acc.status === 'active' && <span style={{ color: '#10b981', fontWeight: 700 }}>✔</span>}
                                    </div>

                                    {/* Inline Actions */}
                                    <div style={{ display: 'flex', gap: '0.3rem' }}>
                                      <button onClick={() => openLedger(acc)} style={{ background: '#eff6ff', border: 'none', color: '#2563eb', borderRadius: '4px', padding: '0.15rem 0.4rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>Ledger</button>
                                      <button onClick={() => openEditModal(acc)} style={{ background: '#f1f5f9', border: 'none', color: '#475569', borderRadius: '4px', padding: '0.15rem 0.4rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>Edit</button>
                                      <button onClick={() => handleDeleteAccount(acc.id)} style={{ background: '#fee2e2', border: 'none', color: '#dc2626', borderRadius: '4px', padding: '0.15rem 0.4rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>Del</button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* ── TABULAR VIEW ── */
        <div className="table-content-card" style={{ padding: '1.25rem', background: '#fff', borderRadius: '12px' }}>
          <div className="coa-list-fullwidth">
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', width: '100%', maxWidth: '420px' }}>
                <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '0.35rem 0.6rem', flex: 1, background: '#fff' }}>
                  <input
                    placeholder="Search accounts..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ border: 'none', outline: 'none', width: '100%', fontSize: '0.9rem' }}
                  />
                  <span style={{ color: '#94a3b8' }}>🔍</span>
                </div>
                <select
                  value={filterType}
                  onChange={e => setFilterType(e.target.value)}
                  style={{ padding: '0.4rem', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '0.85rem' }}
                >
                  {typeNames.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>

              {accounts.length === 0 && (
                <button className="acc-btn-secondary" onClick={async () => {
                  if (!window.confirm('Create default chart of accounts for your business?')) return;
                  try {
                    await accountingApi.createDefaultAccounts();
                    toast.success('Default chart of accounts created successfully!');
                    loadData();
                  } catch (e) { toast.error(e.message); }
                }}>
                  Add Default Accounts
                </button>
              )}
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="coa-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Account Name</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Balance</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(a => (
                    <tr key={a.id}>
                      <td><span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{a.gl_code || a.code || '—'}</span></td>
                      <td style={{ fontWeight: 500 }}>{a.name}</td>
                      <td><span className={`acc-badge ${typeBadge(a.account_primary_type || a.account_sub_type || a.account_type?.name || a.type || '')}`}>{a.account_primary_type || a.account_sub_type || a.account_type?.name || a.type || '—'}</span></td>
                      <td>
                        <span style={{
                          padding: '0.2rem 0.5rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700,
                          background: a.status === 'active' ? '#dcfce7' : '#fee2e2',
                          color: a.status === 'active' ? '#15803d' : '#b91c1c'
                        }}>
                          {a.status || 'active'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(a.current_balance ?? a.balance ?? 0)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center' }}>
                          <button onClick={() => openLedger(a)}
                            style={{ background: 'var(--acc-primary-light)', border: 'none', color: 'var(--acc-primary)', borderRadius: '6px', padding: '0.35rem 0.8rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
                          >
                            Ledger
                          </button>
                          <button onClick={async () => {
                            try {
                              await accountingApi.activateDeactivateAccount(a.id);
                              toast.success(`Account ${a.status === 'active' ? 'disabled' : 'enabled'} successfully!`);
                              loadData();
                            } catch (e) { toast.error(e.message); }
                          }}
                            style={{ background: 'none', border: '1px solid var(--acc-border)', borderRadius: '6px', padding: '0.35rem 0.6rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
                          >
                            {a.status === 'active' ? 'Disable' : 'Enable'}
                          </button>
                          <button onClick={() => openEditModal(a)}
                            style={{ background: 'none', border: '1px solid var(--acc-border)', borderRadius: '6px', padding: '0.35rem 0.6rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
                          >
                            Edit
                          </button>
                          <button onClick={() => handleDeleteAccount(a.id)}
                            style={{ background: 'none', border: '1px solid #fca5a5', color: '#ef4444', borderRadius: '6px', padding: '0.35rem 0.6rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
                          >
                            Del
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={6}><div className="acc-empty"><div className="acc-empty-icon">📂</div><h4>No accounts found</h4><p>Try adjusting your search criteria</p></div></td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Account Modal */}
      {showModal && (
        <div
          onClick={() => setShowModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'var(--acc-surface)', borderRadius: '8px', width: '95%', maxWidth: '480px', padding: '1.5rem', boxShadow: '0 25px 50px rgba(0,0,0,0.2)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', borderBottom: '1px solid var(--acc-border)', paddingBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--acc-text-main)' }}>
                {editAccount ? 'Edit Account' : 'Add Account'}
              </h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--acc-text-muted)' }}>✕</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.85rem', fontWeight: 600, color: 'var(--acc-text-main)' }}>Account Name *</label>
                <input
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Sales Revenue"
                  style={{ width: '100%', padding: '0.55rem', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.85rem', fontWeight: 600, color: 'var(--acc-text-main)' }}>GL Code</label>
                <input
                  value={form.gl_code}
                  onChange={e => setForm(p => ({ ...p, gl_code: e.target.value }))}
                  placeholder="e.g. 4001"
                  style={{ width: '100%', padding: '0.55rem', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.85rem', fontWeight: 600, color: 'var(--acc-text-main)' }}>Account Type</label>
                <select
                  value={form.account_type_id}
                  onChange={e => setForm(p => ({ ...p, account_type_id: e.target.value }))}
                  style={{ width: '100%', padding: '0.55rem', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none' }}
                >
                  {accountTypes.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.name || t.label || t.account_type} ({t.account_primary_type || 'Account'})
                    </option>
                  ))}
                </select>
              </div>

              {!editAccount && (
                <div>
                  <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.85rem', fontWeight: 600, color: 'var(--acc-text-main)' }}>Opening Balance</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.opening_balance}
                    onChange={e => setForm(p => ({ ...p, opening_balance: e.target.value }))}
                    placeholder="0.00"
                    style={{ width: '100%', padding: '0.55rem', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none' }}
                  />
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--acc-border)' }}>
              <button onClick={() => setShowModal(false)} style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button onClick={handleSaveAccount} disabled={saving} style={{ padding: '0.5rem 1.25rem', borderRadius: '6px', border: 'none', background: '#4f46e5', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                {saving ? 'Saving...' : 'Save Account'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ChartOfAccounts;
